/**
 * Central Alert Actions (cloud) — one place for every site's alert notifications.
 *
 *   Site rules              each site's alert rules with Dashboard / Email / Message API and
 *                           who sends them — the site itself, or the cloud ("Via Cloud").
 *                           Edited through the existing per-site rule proxy.
 *   Cloud delivery channels the cloud's own Email (SMTP) and Message API Server. Every alert a
 *                           site relays "via Cloud" goes out through these, plus their
 *                           "Always also notify" recipients.
 *   Delivery log            what each relayed alert asked for and what happened.
 *
 * Estate-wide: never routed through one site's tunnel (not in nodeScopedPaths).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CloudIcon,
  BuildingOffice2Icon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PageTitle,
  PageTabs,
  Callout,
  StatusPill,
  DeliveryChannelCard,
  RuleNotificationTable,
  alertActionsErrorText,
  splitRecipients,
  type AlertActionRule,
  type AlertChannelKey,
  type AlertChannelState,
  type AlertChannelsApi,
} from '@trinetra/shared';
import {
  getNodeAlertConfigs,
  patchNodeAlertConfig,
  getCentralAlertActions,
  saveCentralAlertChannel,
  testCentralAlertChannel,
  getCentralAlertLog,
  connectCentralGmail,
} from '../services/api.service';
import { eventTypeLabel, scopeOf } from '../constants/alertRules';

type Tab = 'rules' | 'channels' | 'log';

interface SiteRules {
  loading: boolean;
  error: string;
  rules: AlertActionRule[];
}

const channelsApi: AlertChannelsApi = {
  saveChannel: (c, body) => saveCentralAlertChannel(c, body),
  testChannel: (c, body) => testCentralAlertChannel(c, body),
  // Gmail API sender: the cloud's way round hosts that block outbound SMTP (DigitalOcean).
  gmailConnect: (redirectUri) => connectCentralGmail(redirectUri),
};

const ruleTitle = (rule: AlertActionRule) => rule.name || `Rule: ${eventTypeLabel(rule.event_type)}`;

function describe(rule: AlertActionRule) {
  const scope = scopeOf(rule);
  const where = scope === 'camera' ? 'One camera' : scope === 'group' ? 'One group' : 'All cameras';
  const sev = rule.severity && rule.severity !== '*' ? ` · ${rule.severity}` : '';
  return `${eventTypeLabel(rule.event_type)} · ${where}${sev}`;
}

const sendsViaCloud = (r: AlertActionRule) => r.notify_via === 'cloud' && (!!r.email_enabled || !!r.message_api_enabled);

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  sent: 'success', partial: 'warning', failed: 'danger', pending: 'neutral',
};

export default function CentralAlertActionsPage() {
  const { nodes } = useNode();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('alerts.config');

  const [tab, setTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'channels' || t === 'log' ? t : 'rules';
  });
  const [channels, setChannels] = useState<Record<AlertChannelKey, AlertChannelState> | null>(null);
  const [relayed24h, setRelayed24h] = useState<Record<string, { total: number; failed: number }>>({});
  const [overviewError, setOverviewError] = useState('');
  const [sites, setSites] = useState<Record<string, SiteRules>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [log, setLog] = useState<any[] | null>(null);
  const [logSite, setLogSite] = useState('');
  const [logError, setLogError] = useState('');
  // Outcome of "Connect Google account" — Google redirects back with ?gmail=connected / ?gmail_error=.
  const [gmailNotice, setGmailNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('gmail') === 'connected') {
      return { tone: 'success', text: 'Google account connected. Turn Email on below and send a test.' };
    }
    const err = q.get('gmail_error');
    return err ? { tone: 'danger', text: `Google account not connected: ${err}` } : null;
  });

  // Drop the one-shot ?gmail… parameters so a reload does not repeat the notice.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('gmail') || url.searchParams.has('gmail_error')) {
      url.searchParams.delete('gmail');
      url.searchParams.delete('gmail_error');
      window.history.replaceState(null, '', url);
    }
  }, []);

  const sortedNodes = useMemo(
    () => [...nodes].sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === 'online' ? -1 : 1)),
    [nodes],
  );

  const loadOverview = useCallback(async () => {
    try {
      const ov = await getCentralAlertActions();
      setChannels(ov.channels);
      setRelayed24h(Object.fromEntries((ov.relayed_24h || []).map((r: any) => [r.node_id, r])));
      setOverviewError('');
    } catch (err) {
      setOverviewError(alertActionsErrorText(err, 'Failed to load Central Alert Actions'));
    }
  }, []);

  const loadSite = useCallback(async (nodeId: string) => {
    setSites((s) => ({ ...s, [nodeId]: { rules: s[nodeId]?.rules || [], loading: true, error: '' } }));
    try {
      const rules = await getNodeAlertConfigs(nodeId);
      setSites((s) => ({ ...s, [nodeId]: { rules, loading: false, error: '' } }));
    } catch (err) {
      setSites((s) => ({ ...s, [nodeId]: { rules: [], loading: false, error: alertActionsErrorText(err, 'Could not read this site’s rules') } }));
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      setLog(await getCentralAlertLog({ nodeId: logSite || undefined, limit: 200 }));
      setLogError('');
    } catch (err) {
      setLogError(alertActionsErrorText(err, 'Failed to load the delivery log'));
    }
  }, [logSite]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Every online site's rules, once — the site list shows how many relay via cloud.
  const onlineIds = sortedNodes.filter((n) => n.status === 'online').map((n) => n.nodeId).join(',');
  useEffect(() => {
    onlineIds.split(',').filter(Boolean).forEach((id) => {
      if (!sites[id]) loadSite(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineIds, loadSite]);

  useEffect(() => {
    if (!selected && sortedNodes.length) setSelected(sortedNodes[0].nodeId);
  }, [selected, sortedNodes]);

  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

  const changeTab = (next: string) => {
    setTab(next as Tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.replaceState(null, '', url);
  };

  const refresh = () => {
    loadOverview();
    if (selected) loadSite(selected);
    if (tab === 'log') loadLog();
  };

  const patchRule = (nodeId: string) => async (rule: AlertActionRule, patch: Record<string, unknown>) => {
    const updated = await patchNodeAlertConfig(nodeId, rule.id, patch);
    setSites((s) => ({
      ...s,
      [nodeId]: { ...s[nodeId], rules: (s[nodeId]?.rules || []).map((r) => (r.id === rule.id ? { ...r, ...(updated || patch) } : r)) },
    }));
    return updated;
  };

  const site = selected ? sortedNodes.find((n) => n.nodeId === selected) : undefined;
  const siteState = selected ? sites[selected] : undefined;
  const totalViaCloud = Object.values(sites).reduce((n, s) => n + s.rules.filter(sendsViaCloud).length, 0);
  const channelsOff = channels ? (['email', 'message_api'] as AlertChannelKey[]).filter((c) => !channels[c]?.enabled) : [];
  const cloudNeeds = Object.values(sites).flatMap((s) => s.rules).filter((r) => sendsViaCloud(r) && r.is_active !== false
    && ((r.email_enabled && channelsOff.includes('email')) || (r.message_api_enabled && channelsOff.includes('message_api'))));

  return (
    <div className="space-y-3">
      <PageTitle title="Central Alert Actions" subtitle="Cloud email & messages for every site's alerts">
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-muted"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </PageTitle>

      {overviewError && <Callout tone="danger">{overviewError}</Callout>}

      <PageTabs
        active={tab}
        onChange={changeTab}
        tabs={[
          { key: 'rules', label: 'Site rules' },
          { key: 'channels', label: 'Cloud delivery channels' },
          { key: 'log', label: 'Delivery log' },
        ]}
      />

      {tab === 'rules' && (
        <div className="space-y-3">
          {cloudNeeds.length > 0 && (
            <Callout tone="warning">
              {cloudNeeds.length} rule{cloudNeeds.length > 1 ? 's' : ''} across your sites send{cloudNeeds.length > 1 ? '' : 's'}{' '}
              {channelsOff.map((c) => (c === 'email' ? 'email' : 'messages')).join(' / ')} via Cloud, but the cloud&apos;s{' '}
              {channelsOff.length > 1 ? 'channels are' : 'channel is'} off.{' '}
              <button type="button" className="font-medium underline" onClick={() => changeTab('channels')}>Set up cloud delivery channels</button>
            </Callout>
          )}

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
            {/* Sites */}
            <aside className="rounded-xl border border-border bg-card">
              <div className="border-b border-border px-3 py-2.5">
                <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Sites</h3>
                <p className="text-[11px] text-muted-foreground">{totalViaCloud} rule{totalViaCloud === 1 ? '' : 's'} send via Cloud</p>
              </div>
              {sortedNodes.length === 0 ? (
                <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">No sites registered yet.</p>
              ) : (
                <ul className="max-h-[60vh] overflow-y-auto py-1">
                  {sortedNodes.map((n) => {
                    const st = sites[n.nodeId];
                    const via = st ? st.rules.filter(sendsViaCloud).length : null;
                    const recent = relayed24h[n.nodeId];
                    const active = n.nodeId === selected;
                    return (
                      <li key={n.nodeId}>
                        <button
                          type="button"
                          onClick={() => { setSelected(n.nodeId); if (!sites[n.nodeId] && n.status === 'online') loadSite(n.nodeId); }}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left ${active ? 'bg-brand-light' : 'hover:bg-muted'}`}
                        >
                          {n.status === 'online'
                            ? <BuildingOffice2Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? 'text-brand' : 'text-muted-foreground'}`} />
                            : <SignalSlashIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                          <span className="min-w-0 flex-1">
                            <span className={`block truncate text-[13px] ${active ? 'font-medium text-foreground' : 'text-foreground'}`}>{n.name}</span>
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {n.status !== 'online'
                                ? 'Offline'
                                : st?.loading && !st.rules.length ? 'Loading…'
                                  : st?.error ? 'Rules unavailable'
                                    : `${st?.rules.length ?? 0} rules · ${via ?? 0} via Cloud`}
                              {recent ? ` · ${recent.total} relayed 24 h${recent.failed ? ` (${recent.failed} failed)` : ''}` : ''}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </aside>

            {/* Selected site's rules */}
            <section className="min-w-0 rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-[14px] font-semibold text-foreground">{site ? site.name : 'Pick a site'}</h3>
                  <p className="text-[12px] text-muted-foreground">
                    Per rule: Dashboard on/off, Email and Message API, and whether <span className="text-foreground">the site</span> or{' '}
                    <span className="text-foreground">the cloud</span> sends them. New rules start with Dashboard only.
                  </p>
                </div>
                {site && (
                  <StatusPill tone={site.status === 'online' ? 'success' : 'neutral'}>{site.status === 'online' ? 'Online' : 'Offline'}</StatusPill>
                )}
              </div>
              {!site ? null : site.status !== 'online' ? (
                <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">
                  This site is offline — its rules can be read and changed once it reconnects. Alerts it already relayed are in the Delivery log.
                </p>
              ) : siteState?.error ? (
                <div className="p-4"><Callout tone="danger">{siteState.error}</Callout></div>
              ) : (
                <RuleNotificationTable
                  rules={siteState?.rules || []}
                  loading={!siteState || (siteState.loading && siteState.rules.length === 0)}
                  canEdit={canEdit}
                  perspective="central"
                  titleOf={ruleTitle}
                  describe={describe}
                  onPatch={patchRule(site.nodeId)}
                  emptyText="This site has no alert rules yet."
                />
              )}
            </section>
          </div>
        </div>
      )}

      {tab === 'channels' && (
        <div className="space-y-3">
          {gmailNotice && <Callout tone={gmailNotice.tone}>{gmailNotice.text}</Callout>}
          <Callout tone="info">
            These channels send the email and messages for every rule, on every site, that is set to <span className="font-medium">Via Cloud</span>.
            Rules that send from the site use that site&apos;s own Alert Actions channels instead.
          </Callout>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {(['email', 'message_api'] as AlertChannelKey[]).map((c) => (
              <DeliveryChannelCard
                key={c}
                channel={c}
                state={channels?.[c] || null}
                api={channelsApi}
                perspective="central"
                canEdit={canEdit && !!channels}
                onSaved={(next) => setChannels((prev) => (prev ? { ...prev, [c]: next } : prev))}
              />
            ))}
          </div>
        </div>
      )}

      {tab === 'log' && (
        <div className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            <CloudIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="flex-1 text-[14px] font-semibold text-foreground">Alerts relayed to the cloud</h3>
            <select
              value={logSite}
              onChange={(e) => setLogSite(e.target.value)}
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground"
            >
              <option value="">All sites</option>
              {sortedNodes.map((n) => <option key={n.nodeId} value={n.nodeId}>{n.name}</option>)}
            </select>
          </div>
          {logError && <div className="p-3"><Callout tone="danger">{logError}</Callout></div>}
          {log === null ? (
            <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-muted" />)}</div>
          ) : log.length === 0 ? (
            <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">No alerts relayed yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted">
                  <tr>
                    {['Time', 'Site', 'Alert', 'Camera', 'Sent to', 'Result'].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {log.map((row) => {
                    const results: any[] = Array.isArray(row.results) ? row.results : [];
                    const errors = results.filter((r) => !r.ok && r.error).map((r) => `${r.channel === 'email' ? 'Email' : 'Message API'}: ${r.error}`);
                    return (
                      <tr key={row.id} className="align-top hover:bg-muted">
                        <td className="whitespace-nowrap px-3 py-2.5 text-[12px] text-muted-foreground">{new Date(row.received_at).toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-[13px] text-foreground">{row.node_name || row.node_id}</td>
                        <td className="max-w-[16rem] px-3 py-2.5">
                          <div className="truncate text-[13px] text-foreground">{row.alert_name || row.rule_name || eventTypeLabel(row.event_type)}</div>
                          <div className="truncate text-[11px] text-muted-foreground">{[row.rule_name, row.severity].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className="px-3 py-2.5 text-[12px] text-foreground">{row.camera_name || '—'}</td>
                        <td className="max-w-[16rem] px-3 py-2.5 text-[12px] text-foreground">
                          {Object.entries(row.requested || {}).map(([c, list]) => (
                            <div key={c} className="truncate" title={splitRecipients((list as string[]).join(',')).join(', ')}>
                              <span className="text-muted-foreground">{c === 'email' ? 'Email' : 'Message API'}:</span> {(list as string[]).length}
                            </div>
                          ))}
                        </td>
                        <td className="max-w-[20rem] px-3 py-2.5">
                          <StatusPill tone={STATUS_TONE[row.status] || 'neutral'}>{row.status}</StatusPill>
                          {errors.length > 0 && <div className="mt-1 text-[11px] text-destructive">{errors.join(' · ')}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
